import * as sentry from '../../src/utils/sentry'
import { timeout } from '../../../shared/lib/timeout.no-deps'
import { IDB_flowReports, IDB_photos } from '../IDB/IDB.dexie'
import { Rpts_openReportFetch } from '../Rpts/Rpts.OpenReport'
import type { Rpts_ClientId } from '../Rpts/Rpts.types'
import { isOnline, Serv_send } from '../Serv/Serv.socketClient'
import {
    type PhotoFileName,
    type ReportId as FlowReportId,
    createFileName,
} from '../../../shared/domain/flows/types.no-deps'
import { generateFileId } from '../../../shared/domain/files/types.no-deps'
import { addDotPrefix } from '../../../shared/domain/files/fileExtensions.no-deps'

export interface Imag_Photo {
    fileName: PhotoFileName
    dataURI: string
    extension: 'png' | 'jpg' | 'jpeg'
    status: 'pendingUpload' | 'uploaded'
    reportClientId: FlowReportId | Rpts_ClientId
}

/**
 * @deprecated Use methods in the Uplo folder instead
 */
export async function Imag_savePhoto(
    reportClientId: FlowReportId | Rpts_ClientId,
    dataURI: string
): Promise<PhotoFileName> {
    // Extract file extention from data URI
    const extension = dataURI.match(/^data:image\/([^;]+);/)[1] as 'png' | 'jpg' | 'jpeg'

    // Create a unique fileName for the photo using a uuid
    const fileName = createFileName(generateFileId(), addDotPrefix(extension))

    // Save the photo to IDB
    await IDB_photos().put({ fileName, dataURI, extension, status: 'pendingUpload', reportClientId })

    // Start uploading photo continuously
    startUploadingPhoto(fileName)

    return fileName
}

export async function Imag_startUploadingPhotos() {
    const fileNames = await IDB_photos().orderBy('fileName').primaryKeys()

    for (const fileName of fileNames) {
        startUploadingPhoto(fileName)
    }
}

/**
 * Continues to attempt to upload every 10 seconds until successful
 */
async function startUploadingPhoto(fileName: PhotoFileName) {
    try {
        // Only attempt if online
        if (await isOnline()) {
            // Re-read from disk to make sure it's there and to avoid keeping it in memory
            const photo = await IDB_photos().get(fileName)

            // Do not keep recursing if the photo has been deleted
            if (photo == null) return

            // Do not keep recursing if the photo has already been uploaded
            if (photo.status === 'uploaded') return

            // Extract the photo content from data URI
            const content = photo.dataURI.substring(photo.dataURI.indexOf(',') + 1)

            // Try to upload
            await Serv_send('file.put', { fileName: photo.fileName, file: content, type: photo.extension })

            // Update the uploaded field
            await IDB_photos().put({ ...photo, status: 'uploaded' })

            // Do no keep recursing if succeeded
            return
        }
    } catch (error) {
        sentry.err(error, { extra: { fileName } })
    }

    // Wait 10 seconds
    await timeout(10000)

    // Try again
    startUploadingPhoto(fileName)
}

export async function Imag_photosPrune() {
    const openReportClientIds: Array<FlowReportId | Rpts_ClientId> = []

    // Get client id of open activity report
    const openReport = await Rpts_openReportFetch()
    if (openReport != null) {
        openReportClientIds.push(openReport.clientId)
    }

    // Get client id of open flow reports
    await IDB_flowReports()
        .where('status')
        .equals('open')
        .each((report) => openReportClientIds.push(report.id))

    // Get the uploaded photos that are not on any open report
    const photosToDelete: PhotoFileName[] = []
    await IDB_photos()
        .where('status')
        .equals('uploaded')
        .each((photo) => {
            if (openReportClientIds.includes(photo.reportClientId)) return
            photosToDelete.push(photo.fileName)
        })

    // Delete them
    await IDB_photos().bulkDelete(photosToDelete)
}
